/**
 * One LEGACY visit-note type's administration card.
 *
 * Binds two independent things for a single note type:
 *  1. the ai_template keyed `visit-note.<type>` — its active version's
 *     instructions and model alias, plus append-only version history/rollback,
 *     all of which lives in AiPromptTemplateCard;
 *  2. the visit_note_settings row — which chart sources feed the prompt.
 *
 * The template side is now AiPromptTemplateCard, extracted verbatim because the
 * canonical prompts need the same card under a different key (this file's old
 * header already said "nothing here is visit-note-specific except the key"). The
 * settings side is lifted to VisitNotesPage, which owns the mutation — this card
 * only reports the NEXT full settings object.
 *
 * LEGACY, and deliberately kept. care-management-gql resolves these four keys for
 * requests that pass the deprecated `noteType` instead of a canonical
 * (format, standard) shape, so a prompt an admin has tuned here still serves
 * exactly the requests it always did. The prompts on the CANONICAL path are
 * administered by CanonicalNotePromptSection.
 */
import {
  VISIT_NOTE_TEMPLATE_KEYS,
  VISIT_NOTE_TYPE_LABELS,
  type VisitNoteSettings,
  type VisitNoteType,
} from "../graphql/adminOps";
import { AiPromptTemplateCard } from "./AiPromptTemplateCard";
import { Toggle } from "./Toggle";

/** Display order + copy for the data-source toggles. `key` matches VisitNoteSettings 1:1. */
const SOURCES: {
  key: Exclude<keyof VisitNoteSettings, "noteType" | "updatedBy">;
  label: string;
  description: string;
}[] = [
  {
    key: "isProblemsEnabled",
    label: "Problems",
    description: "Active problem list.",
  },
  {
    key: "isMedicationsEnabled",
    label: "Medications",
    description: "Current medication list.",
  },
  {
    key: "isAllergiesEnabled",
    label: "Allergies",
    description: "Allergies and intolerances.",
  },
  {
    key: "isVitalsEnabled",
    label: "Vitals",
    description: "Vitals recorded for this encounter.",
  },
  {
    key: "isCareGapsEnabled",
    label: "Care gaps",
    description: "Open care gaps — useful for MEAT, noise for a simple visit.",
  },
  {
    key: "isPriorNotesEnabled",
    label: "Prior notes",
    description: "Text of previous signed notes. Adds context and cost.",
  },
];

export interface VisitNoteTypeCardProps {
  noteType: VisitNoteType;
  settings: VisitNoteSettings;
  modelAliases: string[];
  /** Receives the COMPLETE next settings row, never a patch. */
  onSettingsChange: (next: VisitNoteSettings) => void;
  /** True while the page's settings mutation is in flight. */
  savingSettings?: boolean;
}

export function VisitNoteTypeCard({
  noteType,
  settings,
  modelAliases,
  onSettingsChange,
  savingSettings = false,
}: VisitNoteTypeCardProps) {
  return (
    <AiPromptTemplateCard
      templateKey={VISIT_NOTE_TEMPLATE_KEYS[noteType]}
      title={VISIT_NOTE_TYPE_LABELS[noteType]}
      idPrefix={`vn-${noteType.toLowerCase()}`}
      testId={`card-${noteType}`}
      modelAliases={modelAliases}
      seedMigration="0013_visit_note_templates.sql"
      footer={
        /* Data sources. Keyed by the LEGACY note type, which is also what the
           canonical path looks its gate up by (settingsNoteTypeFor collapses a
           canonical shape onto these same rows), so this control governs both
           paths — the canonical cards deliberately do not duplicate it. */
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold m-0 text-muted-foreground">
            Data sources
          </h3>
          {SOURCES.map((source) => (
            <div
              key={source.key}
              className="flex items-center gap-4 rounded-lg border border-border px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-xs m-0">{source.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 m-0">
                  {source.description}
                </p>
              </div>
              <Toggle
                label={source.label}
                enabled={settings[source.key]}
                disabled={savingSettings}
                // Emit the WHOLE row: the backend mutation replaces it, so a
                // partial patch would reset every untouched flag.
                onChange={(v) =>
                  onSettingsChange({ ...settings, [source.key]: v })
                }
              />
            </div>
          ))}
          {settings.updatedBy && (
            <p className="m-0 text-[10px] text-muted-foreground/60">
              Sources last updated by {settings.updatedBy}
            </p>
          )}
        </div>
      }
    />
  );
}

export default VisitNoteTypeCard;
